<template>
  <c-loading-area :loading="initialLoad" :height="350" :card="false">
    <transition name="scroll-x-reverse-transition" mode="out-in">
      <v-card v-if="hasSeenStep" key="origCourtCasesTable">
        <v-card-title>
          {{ $t('page.manageFiling.origCourt.table.title') }}
        </v-card-title>
        <v-card-subtitle>
          {{ $t('page.manageFiling.origCourt.table.description') }}
        </v-card-subtitle>
        <v-data-table
          :items="origCourtCases"
          :headers="headers"
          :server-items-length="page.totalElements"
          :loading="loading"
          class="px-4"
          item-key="resourceID"
          disable-sort
          hide-default-footer
          :mobile-breakpoint="800">
          <template #item.originatingJudgments="{ value }">
            <div v-for="(judgment, index) in value"
                 :key="`judgment-${index}`"
                 :class="{'mt-2': index!==0 }">
              <div v-if="hasJudgmentText(judgment)">
                {{ generateJudgmentText(judgment.judgeName, judgment.judgmentTypeName) }}
              </div>
              <div v-if="!isNil(judgment.judgmentDate)" class="text-minimum-gray">
                {{ $cove.formatDate(judgment.judgmentDate) }}
              </div>
            </div>
          </template>
          <template #item.resourceID="{ value }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-horizontal</v-icon>
                  <span class="d-sr-only">{{ $t('global.action') }}</span>
                </v-btn>
              </template>
              <v-list dense style="min-width:100px">
                <v-list-item class="inset-focus" @click="$emit('edit', value)">
                  <v-list-item-title>
                    {{ $t('global.edit') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item class="inset-focus" @click="remove(value)">
                  <v-list-item-title>
                    {{ $t('global.remove') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template #footer>
            <paging-controls :page="page" @changePage="changePage"/>
          </template>
        </v-data-table>

        <v-card-actions class="card-actions-button-panel row-800 mt-7 pt-3">
          <div>
            <c-btn v-if="isStepComplete" type="primary" block @click="$emit('next')">
              {{ $t('global.continue') }}
            </c-btn>
          </div>
          <div>
            <c-btn block @click="$emit('start')">
              {{ $t('page.manageFiling.origCourt.table.button.addOrigCourtCase') }}
            </c-btn>
          </div>
        </v-card-actions>
      </v-card>

      <orig-court-cases-splash-card v-else
                                    key="splashCard"
                                    @addOrigCourtCase="$emit('start')"
                                    @viewOrigCourtCases="viewOrigCourtCases"
                                    @continue="$emit('next')"/>

    </transition>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';
import OrigCourtCasesSplashCard from '@components/support/manageFiling/originatingCourt/OrigCourtCasesSplashCard';
import PagingControls from '@components/support/PagingControls';
import { generateJudgmentText } from '@/application/utility';

const filingStore = useFilingStore();

export default {
  components: { OrigCourtCasesSplashCard, PagingControls },
  data () {
    return {
      initialLoad: true,
      hasSeenStep: false,
      loading: false,
      origCourtCases: [],
      page: {
        size: 20,
        number: 0,
        totalElements: 0,
        totalPages: 0
      },
      headers: [
        { text: this.$t('page.manageFiling.origCourt.table.column.origCourt'), value: 'courtName', sortable: false },
        { text: this.$t('page.manageFiling.origCourt.table.column.caseNumber'), value: 'caseNumber', sortable: false },
        { text: this.$t('page.manageFiling.origCourt.table.column.caseTitle'), value: 'caseTitle', sortable: false },
        { text: this.$t('page.manageFiling.origCourt.table.column.judgments'), value: 'originatingJudgments', sortable: false },
        { text: this.$t('global.action'), value: 'resourceID', sortable: false, align: 'right' }
      ]
    };
  },
  computed: {
    filingID () {
      return filingStore.filingID;
    },
    isStepComplete () {
      return filingStore.isStepComplete(this.CONST.FILING_STEP_ORIGINATING_COURT);
    }
  },
  created () {
    this.hasSeenStep = filingStore.hasSeenStep;
    if (!this.hasSeenStep)
    {
      filingStore.markStepAsSeen();
    }

    this.loadOrigCourtCases()
      .finally(() => {
        this.initialLoad = false;
      });
  },
  methods: {
    generateJudgmentText,
    hasJudgmentText (judgment) {
      return !_.isNil(judgment.judgeName) || !_.isNil(judgment.judgmentTypeName);
    },
    loadOrigCourtCases () {
      this.loading = true;

      let url = this.$cove.getAPI({
        name: 'api.filing.originatingCourtCases',
        query: {
          filingID: this.filingID,
          size: this.page.size,
          page: this.page.number,
          fields: '*,originatingJudgments(*)'
        }
      });

      return axios.get(url).
        then((response) => {
          this.origCourtCases = _.get(response, 'data._embedded.results', []);
          this.page.number = response.data.page.number;
          this.page.totalPages = response.data.page.totalPages;
          this.page.totalElements = response.data.page.totalElements;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changePage (newPage) {
      this.page.number = newPage;
      this.loadOrigCourtCases();
    },
    viewOrigCourtCases () {
      this.hasSeenStep = true;
    },
    remove (origCourtCaseID) {
      this.$cove.confirm({ message: this.$t('application.remove.confirm') })
        .then((confirmed) => {
          if (confirmed)
          {
            this.$cove.blockNow();
            Filing.removeOrigCourtCase(origCourtCaseID)
              .then(() => {
                let p1 = this.loadOrigCourtCases();
                let p2 = filingStore.toggleSteps({ visible: true, refresh: true });
                return Promise.all([p1, p2]);
              })
              .then(() => {
                this.$cove.notify({ message: this.$t('application.remove.success'), color: 'success' });
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    }
  }
};
</script>